// ActivityHistory/index.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import ActivityHistoryTable from './components/ActivityHistoryTable';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import companyInfo from 'context/companyInfo';
import { UserContext } from 'context/userContext';
import { useNotifications } from 'context/NotificationContext';
import ArgonBox from 'components/ArgonBox';

// --- Import the refactored, secure services ---
import { deleteActivity, getActivity, updateActivitySettings, getActivitySettings } from 'services/activity';

function ActivityHistory() {
  const { userId } = useContext(UserContext);
  const { addNotification } = useNotifications();

  const [isLoading, setIsLoading] = useState(true);
  const [rawLogs, setRawLogs] = useState([]);
  const [processedLogs, setProcessedLogs] = useState([]);
  const [currentTimeoutSetting, setCurrentTimeoutSetting] = useState(24); // Default

  // --- processFetchedLogs remains the same, it's good pure logic ---
  const processFetchedLogs = useCallback((fetchedActivities) => {
    if (!fetchedActivities || fetchedActivities.length === 0) {
      return [];
    }

    const finalStatusMap = new Map();

    // A consistent key generator for ANY activity type.
    const generateActivityKey = (log) => {
      // For batch operations, the batchId is the primary key.
      if (log.operationBatchId) {
        const productKey = `${log.product_name}#${log.product_version}`;
        return `${log.operationBatchId}-${productKey}`;
      }
      // For single operations, a combination of fields creates a unique key.
      return `${log.operation_type}-${log.org_name}-${log.product_name}-${log.product_version}`;
    };

    // Pass 1: Find all logs with a final status ("Success" or "Failed")
    // and store their status in the map.
    fetchedActivities.forEach((log) => {
      if (log.status === 'Success' || log.status === 'Failed') {
        const key = generateActivityKey(log);
        finalStatusMap.set(key, log.status);
      }
    });

    // Pass 2: Filter the list.
    const displayLogs = fetchedActivities.filter((log) => {
      if (log.status === 'In Progress') {
        const key = generateActivityKey(log);
        // If a final status (Success OR Failed) exists in the map for this key...
        if (finalStatusMap.has(key)) {
          return false; // ...then HIDE the "In Progress" log.
        }
      }
      return true; // Keep all other logs
    });

    // Sort the final, filtered list by timestamp.
    return [...displayLogs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, []);

  const fetchActivities = useCallback(
    async (showLoadingNotification = true) => {
      if (!userId) {
        setRawLogs([]);
        setProcessedLogs([]);
        setIsLoading(false);
        return;
      }
      if (showLoadingNotification) {
        addNotification({ title: ['Loading Activities'], details: 'Fetching activity history...', severity: 'info' });
      }
      setIsLoading(true);
      try {
        // NEW PATTERN: The service returns the data object directly or throws an error.
        const { activities } = await getActivity();
        const { settings } = await getActivitySettings();

        console.log('[fetchActivities] activites', activities);
        console.log('[fetchActivities] settings', settings);

        setRawLogs(activities || []);
        setProcessedLogs(processFetchedLogs(activities || []));
        if (settings && settings.deletion_timeout) {
          setCurrentTimeoutSetting(settings.deletion_timeout);
        }
      } catch (err) {
        console.error('Error fetching activity logs:', err);
        addNotification({
          title: ['Fetch Error'],
          details: err.message || 'Failed to load activity history. Please try again.',
          severity: 'error',
        });
        setRawLogs([]);
        setProcessedLogs([]);
      } finally {
        setIsLoading(false);
      }
    },
    [userId, addNotification, processFetchedLogs]
  );

  // --- REFACTORED: handleUpdateTimeoutSetting function ---
  const handleUpdateTimeoutSetting = useCallback(
    async (hours) => {
      if (!userId) {
        addNotification({ title: ['Update Failed'], details: 'User not identified.', severity: 'error' });
        return;
      }
      addNotification({
        title: ['Updating Settings'],
        details: `Setting activity log timeout to ${hours} hours...`,
        severity: 'info',
      });
      try {
        // NEW PATTERN: Service returns data directly or throws.
        await updateActivitySettings(hours);

        addNotification({
          title: ['Settings Updated'],
          details: `Activity log timeout successfully set to ${hours} hours.`,
          severity: 'success',
        });
        setCurrentTimeoutSetting(hours);

        // Trigger cleanup after settings update
        addNotification({
          title: ['Cleanup Initiated'],
          details: 'Running activity log cleanup based on new settings...',
          severity: 'info',
        });

        // Use try/catch for the cleanup call as well
        try {
          await deleteActivity();
          addNotification({
            title: ['Cleanup Successful'],
            details: 'Activity cleanup completed.',
            severity: 'success',
          });
        } catch (cleanupError) {
          addNotification({
            title: ['Cleanup Error'],
            details: cleanupError.message || 'Error during post-setting activity cleanup.',
            severity: 'error',
          });
        } finally {
          // Always refresh the logs to show the effect of the cleanup
          fetchActivities(false);
        }
      } catch (err) {
        console.error('Error updating timeout setting:', err);
        addNotification({
          title: ['Settings Update Error'],
          details: err.message || 'An unexpected error occurred while updating settings.',
          severity: 'error',
        });
      }
    },
    [userId, addNotification, fetchActivities]
  );

  // --- useEffect to fetch on initial load ---
  useEffect(() => {
    if (userId) {
      fetchActivities();
    } else {
      setIsLoading(false);
      setRawLogs([]);
      setProcessedLogs([]);
    }
  }, [userId, fetchActivities]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ActivityHistoryTable
          logs={processedLogs}
          isLoading={isLoading}
          onRefreshLogs={() => fetchActivities(true)}
          currentTimeout={currentTimeoutSetting}
          onUpdateTimeout={handleUpdateTimeoutSetting}
        />
      </ArgonBox>
      <Footer company={companyInfo} links={companyInfo.links} />
    </DashboardLayout>
  );
}

export default ActivityHistory;
